import React from 'react';
import { Button } from './ui/button';
import { Heart } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * FavoriteButton component - adds/removes an item from user's favorites
 * @param {string} itemType - Type of item: "news", "tender", "project", "company"
 * @param {string} itemId - The ID of the item
 * @param {boolean} isFavorite - Whether the item is currently a favorite
 * @param {function} onToggle - Callback when favorite status changes (receives new state)
 * @param {string} token - Auth token
 * @param {string} className - Additional CSS classes
 * @param {boolean} compact - If true, shows smaller button
 */
export default function FavoriteButton({ 
  itemType, 
  itemId, 
  isFavorite = false, 
  onToggle,
  token,
  className = '',
  compact = false
}) {
  const [loading, setLoading] = React.useState(false);
  const [favorite, setFavorite] = React.useState(isFavorite);

  React.useEffect(() => {
    setFavorite(isFavorite);
  }, [isFavorite]);

  const toggleFavorite = async (e) => {
    e.stopPropagation();
    e.preventDefault();
    
    if (!token) {
      toast.error('Please log in to add favorites');
      return;
    }
    
    try {
      setLoading(true);
      
      if (itemType === 'contact') {
        // Contacts use a dedicated endpoint that stores is_favorite on the contact
        const response = await axios.post(
          `${BACKEND_URL}/api/contacts/${itemId}/toggle-favorite`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const newState = !!response?.data?.is_favorite;
        setFavorite(newState);
        if (onToggle) onToggle(newState);
      } else if (favorite) {
        // Remove from favorites
        await axios.delete(
          `${BACKEND_URL}/api/admin/favorites`,
          { 
            headers: { Authorization: `Bearer ${token}` },
            data: { item_type: itemType, item_id: itemId }
          }
        );
        setFavorite(false);
        if (onToggle) onToggle(false);
      } else {
        // Add to favorites
        await axios.post(
          `${BACKEND_URL}/api/admin/favorites`,
          { item_type: itemType, item_id: itemId },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setFavorite(true);
        if (onToggle) onToggle(true);
      }
    } catch (err) {
      console.error('Error toggling favorite:', err);
      toast.error('Failed to update favorites');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button
      variant="ghost"
      size={compact ? "sm" : "default"}
      className={`${compact ? 'h-8 w-8 p-0' : 'h-9 px-3'} ${className}`}
      onClick={toggleFavorite}
      disabled={loading}
      title={favorite ? 'Remove from favorites' : 'Add to favorites'}
      data-testid={`favorite-${itemType}-${itemId}`}
    >
      <Heart 
        className={`${compact ? 'w-4 h-4' : 'w-5 h-5'} transition-colors ${
          favorite 
            ? 'fill-red-500 text-red-500' 
            : 'text-muted-foreground hover:text-red-500'
        }`}
      />
    </Button>
  );
}
